import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { transactionApi, eventApi, transactionCategoriesApi } from '../lib/api';
import { supabase } from '../lib/supabase';

interface NewTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultBusinessArea?: string;
  editTransaction?: {
    id: string;
    type: string;
    amount: number;
    description: string;
    category: string;
    business_area?: string;
    transaction_date: string;
    event_id?: string;
    travel_package_id?: string;
    notes?: string;
  };
}

const BUSINESS_AREAS = [
  { value: 'eventi_single', label: 'Eventi Single' },
  { value: 'agenzia_amore', label: 'Agenzia Amore' },
  { value: 'dating_coach', label: 'Dating Coach' },
  { value: 'viaggi_single', label: 'Viaggi per Single' }
];

const NewTransactionModal: React.FC<NewTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultBusinessArea,
  editTransaction
}) => {
  const [type, setType] = useState<'income' | 'expense'>('income');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [businessArea, setBusinessArea] = useState('eventi_single');
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [eventId, setEventId] = useState('');
  const [notes, setNotes] = useState('');
  const [events, setEvents] = useState<any[]>([]);
  const [travelPackages, setTravelPackages] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadEventsOrTravels();
      if (editTransaction) {
        setType(editTransaction.type as 'income' | 'expense');
        setAmount(editTransaction.amount.toString());
        setDescription(editTransaction.description);
        setCategory(editTransaction.category);
        setBusinessArea(editTransaction.business_area || defaultBusinessArea || 'eventi_single');
        setTransactionDate(editTransaction.transaction_date);
        setEventId(editTransaction.travel_package_id || editTransaction.event_id || '');
        setNotes(editTransaction.notes || '');
      } else {
        resetForm();
      }
    }
  }, [isOpen, editTransaction, defaultBusinessArea]);

  useEffect(() => {
    if (businessArea) {
      loadCategories();
      loadEventsOrTravels();
      setCategory('');
    }
  }, [businessArea, type]);

  const loadEventsOrTravels = async () => {
    try {
      if (businessArea === 'viaggi_single') {
        const { data, error } = await supabase
          .from('travel_packages')
          .select('*')
          .order('start_date', { ascending: false });

        if (error) throw error;
        setTravelPackages(data || []);
        setEvents([]);
      } else {
        const data = await eventApi.getAll();
        setEvents(data || []);
        setTravelPackages([]);
      }
    } catch (err) {
      console.error('Error loading events/travels:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await transactionCategoriesApi.getByBusinessArea(businessArea, type);
      setCategories(data || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const resetForm = () => {
    setType('income');
    setAmount('');
    setDescription('');
    setCategory('');
    setBusinessArea(defaultBusinessArea || 'eventi_single');
    setTransactionDate(new Date().toISOString().split('T')[0]);
    setEventId('');
    setNotes('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!amount || !description || !category || !transactionDate) {
      setError('Compila tutti i campi obbligatori');
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setError('Inserisci un importo valido');
      return;
    }

    setLoading(true);

    try {
      const transactionData: any = {
        type,
        amount: numericAmount,
        description,
        category,
        business_area: businessArea,
        transaction_date: transactionDate,
        notes: notes || null
      };

      if (businessArea === 'viaggi_single') {
        transactionData.travel_package_id = eventId || null;
        transactionData.event_id = null;
      } else {
        transactionData.event_id = eventId || null;
        transactionData.travel_package_id = null;
      }

      if (editTransaction) {
        await transactionApi.update(editTransaction.id, transactionData);
      } else {
        await transactionApi.create(transactionData as any);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving transaction:', err);
      setError(err.message || 'Errore nel salvare la transazione');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">
            {editTransaction ? 'Modifica Transazione' : 'Nuova Transazione'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all">
              <input
                type="radio"
                value="income"
                checked={type === 'income'}
                onChange={(e) => {
                  setType(e.target.value as 'income');
                  setCategory('');
                }}
                className="mr-3 text-emerald-500 focus:ring-emerald-500"
              />
              <div>
                <div className="font-semibold text-gray-900">Entrata</div>
                <div className="text-sm text-gray-500">Incasso</div>
              </div>
            </label>

            <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all">
              <input
                type="radio"
                value="expense"
                checked={type === 'expense'}
                onChange={(e) => {
                  setType(e.target.value as 'expense');
                  setCategory('');
                }}
                className="mr-3 text-rose-500 focus:ring-rose-500"
              />
              <div>
                <div className="font-semibold text-gray-900">Uscita</div>
                <div className="text-sm text-gray-500">Spesa</div>
              </div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Importo <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrizione <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Es: Iscrizioni evento Flash Crush Roma"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categoria <span className="text-red-500">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Seleziona categoria</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {!defaultBusinessArea && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area di Business <span className="text-red-500">*</span>
              </label>
              <select
                value={businessArea}
                onChange={(e) => setBusinessArea(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {BUSINESS_AREAS.map((area) => (
                  <option key={area.value} value={area.value}>
                    {area.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {defaultBusinessArea && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area di Business
              </label>
              <div className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                {BUSINESS_AREAS.find(area => area.value === businessArea)?.label || businessArea}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {businessArea === 'viaggi_single' ? 'Viaggio (opzionale)' : 'Evento (opzionale)'}
            </label>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">
                {businessArea === 'viaggi_single' ? 'Nessun viaggio collegato' : 'Nessun evento collegato'}
              </option>
              {businessArea === 'viaggi_single' ? (
                travelPackages.map((travel) => (
                  <option key={travel.id} value={travel.id}>
                    {travel.title} - {new Date(travel.start_date).toLocaleDateString('it-IT')} / {new Date(travel.end_date).toLocaleDateString('it-IT')}
                  </option>
                ))
              ) : (
                events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name} - {new Date(event.date).toLocaleDateString('it-IT')}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (opzionale)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Note aggiuntive..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Salvataggio...' : editTransaction ? 'Salva Modifiche' : 'Crea Transazione'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTransactionModal;
